import { ACTIVE_DELETIONS_POLL_MS } from '@/hooks/api/assets/useActiveAssetDeletions';
import { Box, LinearProgress, Typography } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

const TICK_MS = 100;

export const useLiveCount = (latestCount: number) => {
  const [shownCount, setShownCount] = useState(latestCount);
  const shownCountReference = useRef(shownCount);
  shownCountReference.current = shownCount;

  useEffect(() => {
    const start = shownCountReference.current;
    if (start === 0) {
      setShownCount(latestCount);
      return;
    }
    const ticksPerPoll = ACTIVE_DELETIONS_POLL_MS / TICK_MS;
    let tick = 0;
    const timer = setInterval(() => {
      tick += 1;
      setShownCount(Math.round(start + ((latestCount - start) * tick) / ticksPerPoll));
      if (tick >= ticksPerPoll) clearInterval(timer);
    }, TICK_MS);
    return () => clearInterval(timer);
  }, [latestCount]);

  return shownCount;
};

export const LiveProgress = ({
  completed,
  total,
  suffix = '',
  variant = 'body2',
}: {
  completed: number;
  total: number;
  suffix?: string;
  variant?: 'body2' | 'caption';
}) => {
  const shownCount = useLiveCount(completed);
  const percent = total > 0 ? (shownCount / total) * 100 : 0;

  if (completed === 0) {
    return (
      <>
        <Typography variant={variant} component='p' color='textSecondary' sx={{ mb: 1 }}>
          Starting...
        </Typography>
        <LinearProgress />
      </>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 1 }}>
        <Typography variant={variant} color='textSecondary' noWrap sx={{ fontVariantNumeric: 'tabular-nums' }}>
          Deleting {shownCount.toLocaleString()} of {total.toLocaleString()}
          {suffix}
        </Typography>
        <Typography variant={variant} color='textSecondary' sx={{ flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
          {Math.floor(percent)}%
        </Typography>
      </Box>
      <LinearProgress variant='determinate' value={percent} />
    </>
  );
};
